// Drives the authorization-code social providers (Discord, Twitch, Kick, X) on the
// player frontend. The button starts a redirect to the provider authorize URL,
// stashing a state + PKCE verifier in sessionStorage; the provider redirects back
// to /auth/:provider/callback, which reads the code and posts it to the backend.
//
// The token exchange and profile fetch happen SERVER-SIDE (the client_secret never
// touches the browser). This file only builds the authorize redirect and keeps the
// per-flow state.

import { getProviderMeta } from "configs/socialProviders";
import { createPkcePair, randomString } from "utils/pkce";

const STORAGE_KEY = "social_oauth_flow";

// The public per-provider config from /initial-settings signup_flow.social_logins.configured
export function getConfiguredProviders(initialSettings) {
  const sf = initialSettings && initialSettings.signup_flow;
  const configured = sf && sf.social_logins && Array.isArray(sf.social_logins.configured)
    ? sf.social_logins.configured
    : [];
  // The configured list is authoritative (the backend only lists enabled +
  // configured providers), independent of the legacy master toggle. Keep only
  // providers we have code-level metadata for.
  return configured.filter((p) => getProviderMeta(p.key));
}

export function getConfiguredProvider(initialSettings, key) {
  return getConfiguredProviders(initialSettings).find((p) => p.key === String(key).toLowerCase()) || null;
}

// Begin an authorization-code redirect for a code-flow provider. `consent`
// carries the 18+/terms acceptance captured before the redirect; it is stored in
// the flow and posted back from the callback so a first-time account is only
// created with consent.
export async function startCodeFlow(providerConfig, consent = {}) {
  const meta = getProviderMeta(providerConfig.key);
  if (!meta || meta.flow !== "code") {
    throw new Error("Not a code-flow provider: " + providerConfig.key);
  }

  const state = randomString(24);
  const scopes = Array.isArray(providerConfig.scopes)
    ? providerConfig.scopes.join(" ")
    : (providerConfig.scopes || "");

  const params = new URLSearchParams({
    client_id: providerConfig.client_id,
    redirect_uri: providerConfig.redirect_uri,
    response_type: "code",
    scope: scopes,
    state: state,
  });

  const flow = {
    provider: meta.key,
    state,
    redirect_uri: providerConfig.redirect_uri,
    agreeToTerms: consent.agreeToTerms ? 1 : 0,
    acceptCountryLimitation: consent.acceptCountryLimitation ? 1 : 0,
  };

  if (meta.needsPkce) {
    const { verifier, challenge } = await createPkcePair();
    flow.code_verifier = verifier;
    params.set("code_challenge", challenge);
    params.set("code_challenge_method", "S256");
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flow));
  window.location.href = meta.authorizeUrl + "?" + params.toString();
}

// Read + clear the stashed flow when the provider redirects back.
export function consumeStoredFlow() {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
